import { createStore } from '@globals/createStore'
import { useCallback } from 'react'
import { useShallow } from 'zustand/shallow'

type DialogDetails<T = unknown> = {
  isOpen: boolean
  dialogData?: T
}

type DialogStoreState = {
  dialogs: Record<string, DialogDetails>
  open: <T>(key: string, data?: T) => void
  close: (key: string) => void
  setDialogData: <T>(key: string, data: T) => void
}

const useDialogStore = createStore<DialogStoreState>(set => ({
  dialogs: {},
  open: (key, data) =>
    set(
      state => ({
        dialogs: { ...state.dialogs, [key]: { isOpen: true, dialogData: data } },
      }),
      false,
      `${key}_Open`,
    ),
  close: key =>
    set(
      state => ({
        dialogs: {
          ...state.dialogs,
          [key]: { ...state.dialogs[key], isOpen: false, dialogData: null },
        },
      }),
      false,
      `${key}_Close`,
    ),
  setDialogData: (key, data) =>
    set(state => ({
      dialogs: {
        ...state.dialogs,
        [key]: {
          ...state.dialogs[key],
          dialogData: data,
        },
      },
    })),
}))

export default useDialogStore

/**
 * Subscribes to the global dialog store for a single dialog `key` and returns
 * its open state, optional typed payload, and imperative helpers.
 *
 * `openDialog`, `closeDialog`, and `setData` are wrapped in `useCallback` with
 * stable store actions (`open`, `close`, `setDialogData`) plus `key`, so their
 * identities stay stable across renders unless `key` changes. Safe to list in
 * React dependency arrays (e.g. `useEffect` cleanups) without spurious reruns
 * or update-depth loops from recreated inline closures.
 *
 * @param key - Unique dialog id; must match the key used by dialog UI / callers.
 * @returns Dialog visibility, payload, and bound open/close/set helpers for this key.
 */
export const useDialog = <T = unknown>(key: string) => {
  const { dialogs, open, close, setDialogData } = useDialogStore(
    useShallow(state => ({
      dialogs: state.dialogs,
      open: state.open,
      close: state.close,
      setDialogData: state.setDialogData,
    })),
  )

  const dialog = (dialogs[key] as DialogDetails<T>) ?? {
    isOpen: false,
    dialogData: undefined,
  }

  const openDialog = useCallback(
    (data?: T) => {
      open<T>(key, data)
    },
    [open, key],
  )

  const closeDialog = useCallback(() => {
    close(key)
  }, [close, key])

  const setData = useCallback(
    (data: T) => {
      setDialogData(key, data)
    },
    [setDialogData, key],
  )

  return {
    isOpen: dialog.isOpen,
    dialogData: dialog.dialogData,
    openDialog,
    closeDialog,
    setData,
  }
}
